import React, { memo } from 'react';
import { Box, Typography, Skeleton, LinearProgress, alpha } from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import { LeaveBalanceItem } from '../../types/dashboardTypes';
import { DashboardCard, DashboardEmptyState, DashboardSkeletonCard, PANEL_SKELETON_SX, useDashboardTones } from './DashboardCard';

interface LeaveBalanceCardProps {
    data?: LeaveBalanceItem[];
    loading?: boolean;
}

/**
 * Leave Balance Card
 * Displays employee's leave balance with progress bars for each leave type
 * Reusable for any employee role (Teacher, Staff, etc.)
 */
const LeaveBalanceCard: React.FC<LeaveBalanceCardProps> = memo(({
    data = [],
    loading = false,
}) => {
    const { theme, isDark } = useDashboardTones();

    // Loading skeleton
    if (loading) {
        return (
            <DashboardSkeletonCard sx={PANEL_SKELETON_SX}>
                <Skeleton variant="text" width={150} height={28} sx={{ mb: 3 }} />
                {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Skeleton variant="text" width={100} />
                            <Skeleton variant="text" width={50} />
                        </Box>
                        <Skeleton variant="rounded" height={8} />
                    </Box>
                ))}
            </DashboardSkeletonCard>
        );
    }

    const hasData = data && data.length > 0;

    return (
        <DashboardCard
            accent="error"
            icon={AccountBalance}
            title="Leave Balance"
            subtitle="Your available leave entitlements"
        >
            {/* Content Area */}
            <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Leave items or Empty State */}
                {hasData ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {data.map((leave) => {
                        const total = leave.allocated + leave.carryForward;
                        const used = leave.used + leave.pending;
                        const progress = total > 0 ? (used / total) * 100 : 0;
                        const leaveColor = leave.color || theme.palette.primary.main;

                        return (
                            <Box key={leave.leaveTypeId}>
                                {/* Leave type name and values */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: leaveColor,
                                            }}
                                        />
                                        <Typography variant="body2" fontWeight={500}>
                                            {leave.leaveTypeName}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            {leave.available}
                                        </Box>
                                        {' / '}{total} remaining
                                    </Typography>
                                </Box>

                                {/* Progress bar */}
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(progress, 100)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: alpha(leaveColor, isDark ? 0.2 : 0.15),
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            backgroundColor: leaveColor,
                                        },
                                    }}
                                />

                                {/* Details row */}
                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                    <Typography variant="caption" color="text.disabled">
                                        Used: {leave.used}
                                    </Typography>
                                    {leave.pending > 0 && (
                                        <Typography variant="caption" color="warning.main">
                                            Pending: {leave.pending}
                                        </Typography>
                                    )}
                                    {leave.carryForward > 0 && (
                                        <Typography variant="caption" color="text.disabled">
                                            Carry Forward: {leave.carryForward}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <DashboardEmptyState
                    size="roomy"
                    accent="secondary"
                    icon={AccountBalance}
                    title="No Leave Balance"
                    description="Your leave balance will appear here once allocated by the administrator."
                />
            )}
            </Box>
        </DashboardCard>
    );
});

LeaveBalanceCard.displayName = 'LeaveBalanceCard';

export default LeaveBalanceCard;
